import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthContext';

interface SupportFormData {
  subject: string;
  message: string;
}

const SupportForm: React.FC = () => {
  const { user, profile } = useAuth();
  const [formData, setFormData] = useState<SupportFormData>({
    subject: 'Support Request',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!profile?.first_name || !profile?.last_name || !user?.email || !formData.message) {
      toast.error('Missing user profile information or message.');
      return;
    }
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(user.email)) {
      toast.error('Your user email is invalid.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Send the email using Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('direct-email', {
        body: {
          to: 'support@roundone.ai',
          templateAlias: 'support-request',
          templateModel: {
            firstName: profile.first_name,
            lastName: profile.last_name,
            email: user.email,
            subject: formData.subject,
            message: formData.message
          },
          from: 'hello@roundone.ai',
          replyTo: user.email
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to send message');
      }

      // Reset form data after successful submission
      setFormData({
        subject: 'Support Request',
        message: ''
      });

      toast.success('Your support request has been sent successfully! Our team will get back to you shortly.');
    } catch (error) {
      console.error('Error sending support form:', error);
      toast.error(`Failed to send your support request. Please try again later. ${error instanceof Error ? error.message : ''}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Submit a Support Request</h3>
      <p className="text-sm text-muted-foreground">
        Fill out the form below and our support team will get back to you as soon as possible.
      </p>
      
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
            Subject
          </label>
          <input
            type="text"
            id="subject"
            value={formData.subject}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-roundone-purple focus:outline-none focus:ring-roundone-purple sm:text-sm"
          />
        </div>
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700">
            Message *
          </label>
          <textarea
            id="message"
            rows={5}
            value={formData.message}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-roundone-purple focus:outline-none focus:ring-roundone-purple sm:text-sm"
            required
          />
        </div>
        <div>
          <Button 
            type="submit"
            className="gradient-button w-full"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Submit Request'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SupportForm; 